"use client";

import * as React from "react";

// Types for autosave system
export interface OnboardingProgress {
  sessionId: string;
  candidateId: string;
  currentStep: string;
  stepIndex: number;
  totalSteps: number;
  joinerType?: 'intern' | 'consultant' | 'employee';
  experienceLevel?: 'fresher' | 'experienced';
  completedSteps: string[];
  formData: Record<string, any>;
  lastSaved: string;
  resumeToken: string;
}

export interface AutosaveState {
  isAutoSaving: boolean;
  lastSavedAt: Date | null;
  hasUnsavedChanges: boolean;
  sessionId: string | null;
}

// Autosave Manager Service
class AutosaveService {
  private static instance: AutosaveService;
  private saveTimeout: NodeJS.Timeout | null = null;
  private readonly AUTOSAVE_DELAY = 2000; // 2 seconds
  private readonly STORAGE_KEY = 'dayone_onboarding_progress';
  
  static getInstance(): AutosaveService {
    if (!AutosaveService.instance) {
      AutosaveService.instance = new AutosaveService();
    }
    return AutosaveService.instance;
  }

  // Generate secure session token (demo implementation)
  private generateSessionToken(): string {
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  // Generate resume token for email links
  private generateResumeToken(): string {
    return `resume_${Date.now()}_${Math.random().toString(36).substr(2, 12)}`;
  }

  // Save progress to localStorage (in real app, this would be an API call)
  saveProgress(progress: Partial<OnboardingProgress>): Promise<void> {
    return new Promise((resolve) => {
      // Clear existing timeout
      if (this.saveTimeout) {
        clearTimeout(this.saveTimeout);
      }

      // Debounce the save operation
      this.saveTimeout = setTimeout(() => {
        try {
          const existingProgress = this.loadProgress();
          const sessionId = existingProgress?.sessionId || this.generateSessionToken();
          
          const fullProgress: OnboardingProgress = {
            sessionId,
            candidateId: progress.candidateId || 'demo_candidate',
            currentStep: progress.currentStep || 'demo',
            stepIndex: progress.stepIndex || 0,
            totalSteps: progress.totalSteps || 7,
            joinerType: progress.joinerType,
            experienceLevel: progress.experienceLevel,
            completedSteps: progress.completedSteps || [],
            formData: { ...existingProgress?.formData, ...progress.formData },
            lastSaved: new Date().toISOString(),
            resumeToken: existingProgress?.resumeToken || this.generateResumeToken()
          };

          localStorage.setItem(this.STORAGE_KEY, JSON.stringify(fullProgress));
          console.log('Progress autosaved:', fullProgress);
          resolve();
        } catch (error) {
          console.error('Failed to save progress:', error);
          resolve(); // Don't fail the user experience
        }
      }, this.AUTOSAVE_DELAY);
    });
  }

  // Load progress from localStorage
  loadProgress(): OnboardingProgress | null {
    try {
      const saved = localStorage.getItem(this.STORAGE_KEY);
      if (saved) {
        return JSON.parse(saved);
      }
    } catch (error) {
      console.error('Failed to load progress:', error);
    }
    return null;
  }

  // Check if there's saved progress
  hasSavedProgress(): boolean {
    return this.loadProgress() !== null;
  }

  // Clear saved progress
  clearProgress(): void {
    localStorage.removeItem(this.STORAGE_KEY);
  }

  // Generate email with resume link (demo)
  sendResumeEmail(progress: OnboardingProgress): Promise<boolean> {
    return new Promise((resolve) => {
      // Simulate API call
      setTimeout(() => {
        console.log('Resume email sent with token:', progress.resumeToken);
        // In real implementation, this would send an actual email
        resolve(true);
      }, 1000);
    });
  }

  // Validate resume token
  validateResumeToken(token: string): OnboardingProgress | null {
    const progress = this.loadProgress();
    if (progress && progress.resumeToken === token) {
      return progress;
    }
    return null;
  }
}

// React Hook for Autosave
export function useAutosave() {
  const [autosaveState, setAutosaveState] = React.useState<AutosaveState>({
    isAutoSaving: false,
    lastSavedAt: null,
    hasUnsavedChanges: false,
    sessionId: null
  });

  const autosaveService = React.useMemo(() => AutosaveService.getInstance(), []);

  // Save progress function
  const saveProgress = React.useCallback(async (progress: Partial<OnboardingProgress>) => {
    setAutosaveState(prev => ({ ...prev, isAutoSaving: true }));
    
    try {
      await autosaveService.saveProgress(progress);
      setAutosaveState(prev => ({
        ...prev,
        isAutoSaving: false,
        lastSavedAt: new Date(),
        hasUnsavedChanges: false,
        sessionId: progress.candidateId || prev.sessionId
      }));
    } catch (error) {
      setAutosaveState(prev => ({ ...prev, isAutoSaving: false }));
    }
  }, [autosaveService]);

  // Load progress function
  const loadProgress = React.useCallback(() => {
    return autosaveService.loadProgress();
  }, [autosaveService]);

  // Check for saved progress
  const hasSavedProgress = React.useCallback(() => {
    return autosaveService.hasSavedProgress();
  }, [autosaveService]);

  // Clear progress
  const clearProgress = React.useCallback(() => {
    autosaveService.clearProgress();
    setAutosaveState({
      isAutoSaving: false,
      lastSavedAt: null,
      hasUnsavedChanges: false,
      sessionId: null
    });
  }, [autosaveService]);

  // Send resume email
  const sendResumeEmail = React.useCallback(async () => {
    const progress = loadProgress();
    if (progress) {
      return await autosaveService.sendResumeEmail(progress);
    }
    return false;
  }, [autosaveService, loadProgress]);

  // Mark as having unsaved changes
  const markUnsavedChanges = React.useCallback(() => {
    setAutosaveState(prev => ({ ...prev, hasUnsavedChanges: true }));
  }, []);

  return {
    autosaveState,
    saveProgress,
    loadProgress,
    hasSavedProgress,
    clearProgress,
    sendResumeEmail,
    markUnsavedChanges
  };
}

// Autosave Context
interface AutosaveContextType {
  autosaveState: AutosaveState;
  saveProgress: (progress: Partial<OnboardingProgress>) => Promise<void>;
  loadProgress: () => OnboardingProgress | null;
  hasSavedProgress: () => boolean;
  clearProgress: () => void;
  sendResumeEmail: () => Promise<boolean>;
  markUnsavedChanges: () => void;
}

const AutosaveContext = React.createContext<AutosaveContextType | null>(null);

export function AutosaveProvider({ children }: { children: React.ReactNode }) {
  const autosave = useAutosave();

  return (
    <AutosaveContext.Provider value={autosave}>
      {children}
    </AutosaveContext.Provider>
  );
}

export function useAutosaveContext() {
  const context = React.useContext(AutosaveContext);
  if (!context) {
    throw new Error('useAutosaveContext must be used within AutosaveProvider');
  }
  return context;
}